import { z } from "zod";

import { DisplayUomOverrideSchema } from "~/shared/documents/schemas/document-display-uom.schema";
import { EditorSchema, type EditorSchemaType, emptyEditorDoc } from "~/utils/types/rich-text";

const optionalBolPdfText = z.string().trim().optional().default("");

const escapeHtmlForPlainBolOverride = (value: string): string => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Legacy Create BOL modal stored plain strings; coerce to TipTap docs on read. */
export const bolPlainTextOverrideToEditorDoc = (text: string): EditorSchemaType => {
  const trimmed = text.trim();
  if (!trimmed) {
    return emptyEditorDoc();
  }
  const htmlBody = escapeHtmlForPlainBolOverride(trimmed).replace(/\r\n|\r|\n/g, "<br>");
  return {
    ...emptyEditorDoc(),
    html: `<p>${htmlBody}</p>`,
    text: trimmed.toLowerCase(),
  };
};

const migrateOptionalEditorOverrideField = (record: Record<string, unknown>, key: "customTextHeader" | "customTextFooter"): void => {
  const value = record[key];
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      delete record[key];
    } else {
      record[key] = bolPlainTextOverrideToEditorDoc(trimmed);
    }
  }
};

export const BolLineEmergencyFlagInputSchema = z.object({
  required: z.boolean().default(false),
  /** Retired issuance note: accept older payloads without carrying text into a new PDF. */
  note: z
    .string()
    .default("")
    .transform(() => ""),
});

/** Per-order-line modal inputs (bucket C) — editable until product metadata wiring replaces manual entry. */
export const BolDocumentLinePdfInputSchema = z.object({
  orderItemId: z.string().uuid(),
  /** Full commodity description block (hazmat + product code + synonym) when org commodity-description slot is enabled. */
  description: z.string().optional(),
  countryOfOrigin: optionalBolPdfText,
  ergNumber: optionalBolPdfText,
  producer: optionalBolPdfText,
  /** Commodity table Pieces cell when org modal slot enabled. */
  pieces: optionalBolPdfText,
  /** Keys match org emergency flag ids — @see docs/OPT_IN_ORDER_DOCUMENTS.md § Bill of Lading */
  emergencyByFlagId: z.record(BolLineEmergencyFlagInputSchema).default({}),
});

const migrateBolLinePdfInputRaw = (line: Record<string, unknown>): void => {
  if (!line.emergencyByFlagId || typeof line.emergencyByFlagId !== "object") {
    const emergencyByFlagId: Record<string, z.infer<typeof BolLineEmergencyFlagInputSchema>> = {};
    if (line.chemtrecRequired === true) {
      emergencyByFlagId.chemtrec = { required: true, note: "" };
    }
    if (line.erapRequired === true) {
      const note = typeof line.erapPlanNumber === "string" ? line.erapPlanNumber : "";
      emergencyByFlagId.erap = { required: true, note };
    }
    line.emergencyByFlagId = emergencyByFlagId;
    delete line.chemtrecRequired;
    delete line.erapRequired;
    delete line.erapPlanNumber;
  }
  const legacyHazmatLine = typeof line.hazmatLine === "string" ? line.hazmatLine.trim() : "";
  if (legacyHazmatLine.length > 0 && typeof line.description !== "string") {
    line.description = legacyHazmatLine;
  }
  delete line.hazmatLine;
  delete line.marinePollutant;
  delete line.cerclaRq;
};

/** Map legacy customText1/2/3 modal fields to header/footer on read. */
export const migrateBolDocumentPdfInputRaw = (raw: unknown): unknown => {
  if (!raw || typeof raw !== "object") {
    return raw;
  }
  const record = raw as Record<string, unknown>;
  if (record.customTextHeader === undefined && typeof record.customText1 === "string") {
    record.customTextHeader = record.customText1;
  }
  if (record.customTextFooter === undefined) {
    if (typeof record.customText3 === "string") {
      record.customTextFooter = record.customText3;
    } else if (typeof record.customText2 === "string") {
      record.customTextFooter = record.customText2;
    }
  }
  migrateOptionalEditorOverrideField(record, "customTextHeader");
  migrateOptionalEditorOverrideField(record, "customTextFooter");
  if (Array.isArray(record.lineItems)) {
    for (const item of record.lineItems) {
      if (item && typeof item === "object") {
        migrateBolLinePdfInputRaw(item as Record<string, unknown>);
      }
    }
  }
  return record;
};

/** Modal-only BOL PDF inputs (bucket C) — not persisted on the order. @see docs/OPT_IN_ORDER_DOCUMENTS.md § Bill of Lading */
export const BolDocumentPdfInputSchema = z.preprocess(
  migrateBolDocumentPdfInputRaw,
  z.object({
    /** Retired actual-carrier annotation; saved carrier identity owns the printed name. */
    carrierDescription: optionalBolPdfText.transform(() => ""),
    carrierContact: optionalBolPdfText,
    customTextHeader: EditorSchema.optional(),
    customTextFooter: EditorSchema.optional(),
    demergeOverride: EditorSchema.optional(),
    demurrageOverride: EditorSchema.optional(),
    additionalTermsOverride: EditorSchema.optional(),
    /** Legacy input accepted for compatibility; saved Order.shipOnBehalf controls built-in BOL identity. */
    thirdPartyPickup: z.boolean().optional(),
    /** Ship-on-behalf BOLs hide the logo by default; explicit false prints it for this document. */
    hideOrgLogo: z.boolean().optional(),
    displayUomOverride: DisplayUomOverrideSchema,
    lineItems: z.array(BolDocumentLinePdfInputSchema).default([]),
  }),
);

export type BolLineEmergencyFlagInput = z.infer<typeof BolLineEmergencyFlagInputSchema>;
export type BolDocumentLinePdfInput = z.infer<typeof BolDocumentLinePdfInputSchema>;
export type BolDocumentPdfInput = z.infer<typeof BolDocumentPdfInputSchema>;

export const emptyBolDocumentPdfInput = (): BolDocumentPdfInput => ({
  carrierDescription: "",
  carrierContact: "",
  lineItems: [],
});

export const getBolLineEmergencyFlagInput = (line: BolDocumentLinePdfInput | undefined, flagId: string): BolLineEmergencyFlagInput =>
  line?.emergencyByFlagId[flagId] ?? { required: false, note: "" };
